import React, { useRef, useState } from "react";
import { Button, Card, Chip, Grid, Stack, Typography } from "@mui/joy";
import IProfile from "../../types/profile.type";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import { GridSaveAltIcon } from "@mui/x-data-grid";
import { useFormik } from "formik";
import * as Yup from "yup";
import { uploadProfileFile } from "../../services/profile.service";
import showNotification from "../utils/Notify";
import CancelIcon from "@mui/icons-material/Cancel";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import { ProfileFileNames } from "../../types/enumeration/ProfileFileNames";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import IconButton from "@mui/joy/IconButton";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import IFile from "../../types/file.type";
import { getById } from "../../services/file.service";
import {downloadFile} from "../../services/file-download.service";

interface Props {
  userId: any;
  profile: IProfile | undefined;
  profileFile: IFile | undefined;
  profileFileName: ProfileFileNames;
  title: string;
  onProfileUpdated?: (p: IProfile) => void;
}

const FileDataForm: React.FC<Props> = (props) => {
  let { profile, profileFile, profileFileName, title, onProfileUpdated} = props;
  const [edit, setEdit] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const initialValues = {
    uploadFile: null as File | null,
  };

  const validationSchema = Yup.object().shape({
    uploadFile: Yup.mixed<File>()
      .nullable()
      .required("Debe agregar un archivo primero.")
      .test(
        "fileSize",
        "El archivo debe ser menor de 5 MB",
        (value) => !value || value.size <= 5 * 1024 * 1024
      ),
  });

  const onSubmit = (data: { uploadFile: File | null }) => {
    if (!profile?.id) {
      showNotification({
        type: "error",
        message: "No se encontró el perfil para asociar el archivo.",
      });
      return;
    }

    if (data.uploadFile) {
      uploadProfileFile(profile.id, data.uploadFile, profileFileName)
        .then((response) => {
          onProfileUpdated?.(response.data);
          showNotification({
            type: "success",
            message: "El archivo se guardó correctamente.",
          });
          setEdit(false);
        })
        .catch((error) => {
          showNotification({
            type: "error",
            message: error?.response?.data?.message ?? "Error al subir el archivo.",
          });
        });
      }
    };

  const formik = useFormik({
    enableReinitialize: false,
    initialValues: initialValues,
    onSubmit,
    validationSchema: validationSchema,
  });

  const handleEditCancelClick = () => {
    if (edit) {
      // Si cancelas, limpia el archivo seleccionado localmente
      formik.setFieldValue("uploadFile", null);
      formik.setFieldTouched("uploadFile", false, false);
      setEdit(false);
    } else {
      setEdit(true);
    }
  };

  return (
    <Card sx={{ gap: 0, marginTop: 2 }}>
      <Typography level="h4" startDecorator={<FolderSharedIcon />}>
        {title}
      </Typography>
      <form className="form" onSubmit={formik.handleSubmit}>
        <Grid container rowSpacing={2} columnSpacing={2} marginBlock={1}>
          <Grid xl={12} md={12} sm={12} xs={12}>
            <Card sx={{ height: 57, padding: 1, margin: 0 }}>
              <Stack direction={"row"} justifyContent={"space-between"}>
                <Stack direction={"row"} justifyContent={"center"}>
                  <Typography sx={{ fontWeight: "bold" }} pt={1} pr={1}>
                    {title}:
                  </Typography>
                  {formik.values.uploadFile ? (
                    <Chip variant="outlined" color="neutral" size="lg">
                      {formik.values.uploadFile.name}
                    </Chip>
                  ) : profileFile ? (
                    <Chip
                      variant="outlined"
                      color="neutral"
                      size="lg"
                      onClick={() => downloadFile(profileFile)}
                      endDecorator={<DownloadForOfflineIcon sx={{ fontSize: 30 }} />}
                    >
                      {profileFile.fileName}
                    </Chip>
                  ) : (
                    <Typography pt={1} pr={1}>No hay archivo adjunto</Typography>
                  )}
                </Stack>

                <Stack direction={"row"} justifyContent={"space-between"}>
                  <input
                    id={`uploadFile-${profileFileName}`}
                    ref={fileInputRef}
                    name="uploadFile"
                    type="file"
                    accept=".pdf,application/pdf"
                    style={{ display: "none" }}
                    onChange={(event) => {
                      if (event.currentTarget.files && event.currentTarget.files.length > 0) {
                        const newFile = event.currentTarget.files[0];
                        formik.setFieldValue("uploadFile", newFile);
                      }
                    }}
                  />

                  <IconButton
                    disabled={!edit}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <CloudUploadIcon sx={{ fontSize: 40 }} />
                  </IconButton>
                </Stack>
              </Stack>

              {formik.touched.uploadFile && formik.errors.uploadFile ? (
                <span className="Error-alert">{formik.errors.uploadFile}</span>
              ) : null}
            </Card>
          </Grid>
        </Grid>

        <Stack direction="row" justifyContent="flex-end" spacing={2} pt={1}>
          <Button
            variant="outlined"
            endDecorator={edit ? <CancelIcon /> : <BorderColorIcon />}
            onClick={handleEditCancelClick}
          >
            {edit ? "Cancelar" : "Editar"}
          </Button>
          <Button type="submit" variant="solid" endDecorator={<GridSaveAltIcon />} disabled={!edit}>
            Guardar
          </Button>
        </Stack>
      </form>
    </Card>
  );
};

export default FileDataForm;